/**
 * A row of multi-select filters for the few columns people slice a list by.
 *
 * FOR the question the grid cannot ask. The community DataGrid holds one column
 * condition, so "level 3 AND structures" - the everyday cut of a requirements
 * list (issue #68) - has no home there. Issue #35 took out a five-menu bar as
 * duplicate machinery; this is the small, client-side half of it that was worth
 * having, and it does NOT bring back the server-side facets. The logic is in
 * quickFilter.ts and is pure; this is only the controls.
 *
 * The component holds no state. The selection is given and every change is
 * reported, because it lives in the URL (a sliced list is a link somebody
 * pastes into an issue) and the screen owns the URL.
 *
 * THE NUMBERS. Each choice shows how many rows picking it would show: counted
 * among the rows that pass the search and the OTHER quick filters. Not this
 * field's own, because a field's counts under its own selection would all read
 * zero except the chosen values - the numbers would describe the current view
 * rather than what a click does, which is the opposite of useful. The list of
 * choices itself comes from ALL rows, so choices do not vanish as the list
 * narrows; a choice that would show nothing stays, reading (0), where it can
 * still be un-picked.
 *
 * WHICH FIELDS APPEAR. A field with fewer than two choices has nothing to slice
 * by, so no control is drawn. A value the URL selects that no row has is added
 * to the choices, though: otherwise a stale link would filter by something
 * invisible, and the chip that lets you remove it would be unreachable.
 */

import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { NONE, applyQuick, quickCounts, quickOptions, withChoices } from './quickFilter'
import type { QuickSelection } from './quickFilter'

export interface QuickField {
  name: string
  /** The control's accessible label, and what it is called on screen. */
  label: string
}

export interface QuickFiltersProps {
  /** Every row: the choices are the distinct values here. */
  rows: object[]
  /**
   * The rows the counts are taken over - those that pass the search box.
   * Defaults to every row, for a screen with no search to apply first.
   */
  counted?: object[]
  fields: QuickField[]
  value: QuickSelection
  onChange: (next: QuickSelection) => void
}

/** What a choice is called: the blank is a choice people can name, not an empty chip. */
function optionLabel(option: string): string {
  return option === NONE ? '(none)' : option
}

export default function QuickFilters({
  rows,
  counted = rows,
  fields,
  value,
  onChange,
}: QuickFiltersProps) {
  const controls = fields.flatMap((field) => {
    const chosen = value[field.name] ?? []
    const options = quickOptions(rows, field.name)
    for (const stale of chosen) if (!options.includes(stale)) options.push(stale)
    if (options.length < 2) return []

    // The rows this field's counts describe: search, and every OTHER field.
    const counts = quickCounts(applyQuick(counted, withChoices(value, field.name, [])), field.name)

    return [{ field, chosen, options, counts }]
  })

  if (controls.length === 0) return null

  return (
    <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1.5 }}>
      {controls.map(({ field, chosen, options, counts }) => (
        <Autocomplete
          key={field.name}
          multiple
          size="small"
          disableCloseOnSelect
          limitTags={2}
          options={options}
          value={chosen}
          getOptionLabel={optionLabel}
          onChange={(_event, next) => onChange(withChoices(value, field.name, next))}
          renderOption={(props, option) => {
            // `key` is pulled out of the spread: React warns when a props
            // object carrying one is spread into JSX.
            const { key, ...rest } = props
            return (
              <li key={key} {...rest}>
                <span>{optionLabel(option)}</span>
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 'auto', pl: 2 }}>
                  {counts.get(option) ?? 0}
                </Typography>
              </li>
            )
          }}
          renderInput={(params) => <TextField {...params} label={field.label} />}
          sx={{ flex: '1 1 220px', maxWidth: 360, minWidth: 200 }}
        />
      ))}
    </Stack>
  )
}
